import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { LogService, ScreenResolution } from 'htv-lib'
import { environment } from '../environments/environment'
import { Router } from '@angular/router'
import { StatusFailureDto, StatusResponseDto, StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { ProjectService } from './project.service'
import { MediaService } from './media.service'
import { LanguageService } from './language.service'
import { HumecloudService } from './humecloud.service'
import { ResolutionService } from './resolution.service'

@Injectable({
  providedIn: 'root',
})
export class StateService {
  readonly HTV_API_URL = environment.htvApiUrl

  isLoading$ = new BehaviorSubject<boolean>(true)
  isProjectLoaded$ = new BehaviorSubject<boolean>(false)

  private resolution!: ScreenResolution

  constructor(
    private logService: LogService,
    private projectService: ProjectService,
    private mediaService: MediaService,
    private humecloudService: HumecloudService,
    private languageService: LanguageService,
    private resolutionService: ResolutionService,
    private router: Router
  ) {}

  async init(projectId: string, generatedProjectId?: string): Promise<void> {
    if (this.isProjectLoaded$.value && this.projectService.getProjectId() === projectId) {
      this.logService.info(`[StateService] Project ${projectId} already loaded, skipping initialization`)
      return
    }

    this.isLoading$.next(true)

    try {
      this.resolution = this.resolutionService.getCurrentResolution()
      const status = await this.retrieveStatus(projectId, this.resolution, generatedProjectId)
      await this.loadStatus(status)
      this.isProjectLoaded$.next(true)

      this.resolutionService.resolutionChanged$.subscribe(newResolution => {
        if (this.resolution !== newResolution && this.isProjectLoaded$.value) {
          this.resolution = newResolution
          this.reloadWithResolution(projectId, newResolution, generatedProjectId)
        }
      })
    } catch (error) {
      await this.router.navigate(['error'], { queryParamsHandling: 'preserve' })
    } finally {
      this.isLoading$.next(false)
    }
  }

  retrieveStatus(
    projectId: string,
    resolution: ScreenResolution = ScreenResolution.RES_1080P,
    generatedProjectId?: string
  ): Promise<StatusSuccessDto> {
    return new Promise((resolve, reject) => {
      this.humecloudService.getProjectStatus(projectId, resolution, generatedProjectId).subscribe({
        next: (response: StatusResponseDto) => {
          if (response.status) {
            const statusSuccess = response as StatusSuccessDto
            resolve(statusSuccess)
          } else {
            const statusFailure = response as StatusFailureDto
            this.logService.error(`[StateService] Invalid status, reason = ${statusFailure.reason}`)
            reject()
          }
        },
        error: () => {
          reject()
        },
      })
    })
  }

  async loadStatus(status: StatusSuccessDto) {
    this.logService.info('[StateService] Initializing application with project data')

    this.mediaService.init(status.medias)
    this.languageService.initFromProject(status.project)
    this.projectService.init(status.project)

    this.logService.info('[StateService] Application initialized successfully')
  }

  async reloadWithResolution(
    projectId: string,
    resolution: ScreenResolution,
    generatedProjectId?: string
  ): Promise<void> {
    this.logService.info(`[StateService] Reloading project with resolution: ${resolution}`)
    this.isLoading$.next(true)

    try {
      const status = await this.retrieveStatus(projectId, resolution, generatedProjectId)
      await this.loadStatus(status)
    } finally {
      this.isLoading$.next(false)
    }
  }
}
